class PieceSspShopTheLook extends HTMLElement {
  connectedCallback() {
    this._initialize();
    this._observeDOM();
  }

  disconnectedCallback() {
    this._teardown();
    if (this._observer) {
      this._observer.disconnect();
      this._observer = null;
    }
  }

  _observeDOM() {
    if (this._observer) return;
    this._observer = new MutationObserver((mutations) => {
      var hasChanges = mutations.some(function (m) {
        return m.type === 'childList' && (m.addedNodes.length > 0 || m.removedNodes.length > 0);
      });
      if (!hasChanges) return;
      // DOM was replaced â€” tear down old bindings and reinitialize
      this._teardown();
      this._initialize();
    });
    this._observer.observe(this, { childList: true, subtree: false });
  }

  _initialize() {
    this.cards = [...this.querySelectorAll('[data-piece-stl-card]')];
    this.grid = this.querySelector('[data-piece-stl-carousel]');
    this.prevButton = this.querySelector('[data-piece-stl-prev]');
    this.nextButton = this.querySelector('[data-piece-stl-next]');

    this.drawers = [...this.querySelectorAll('[data-piece-stl-drawer]')];
    this.drawerOpenButtons = [...this.querySelectorAll('[data-piece-stl-open-drawer]')];
    this.drawerCloseButtons = [...this.querySelectorAll('[data-piece-stl-close]')];

    this.mobileOpeners = [...this.querySelectorAll('[data-mobile-product-open]')];
    this.tooltips = [...this.querySelectorAll('.piece-stl__point')];
    this.popoverByPoint = new WeakMap();
    this.pointByPopover = new WeakMap();
    this.hideTooltipTimers = new WeakMap();
    this.activeTooltipPoint = null;

    this.currentIndex = 0;
    this.isDesignMode = this.dataset.designMode === 'true';
    this.activeDrawer = null;

    this.portalPopovers();

    this.prevButton?.addEventListener('click', () => this.scrollTrack(-1));
    this.nextButton?.addEventListener('click', () => this.scrollTrack(1));

    this.drawerOpenButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const index = Number(button.dataset.lookIndex ?? 0);
        this.openDrawer(index);
      });
    });

    this.drawerCloseButtons.forEach((button) => {
      button.addEventListener('click', () => this.closeDrawer());
    });

    this.drawers.forEach((drawer) => {
      drawer.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.closeDrawer();
      });

      drawer.addEventListener('click', (event) => {
        if (event.target === drawer) {
          this.closeDrawer();
        }
      });

      drawer.addEventListener('close', () => {
        drawer.classList.remove('is-open');
        if (this.activeDrawer === drawer) {
          this.activeDrawer = null;
        }
      });
    });

    this.mobileOpeners.forEach((button) => {
      button.addEventListener('click', (e) => this.handleHotspotTap(e));
    });

    this.tooltips.forEach((point) => {
      point.addEventListener('mouseenter', () => this.showTooltip(point));
      point.addEventListener('mouseleave', () => this.scheduleHideTooltip(point));
      point.addEventListener('focusin', () => this.showTooltip(point));
      point.addEventListener('focusout', (event) => {
        const popover = this.getPopover(point);
        if (popover?.contains(event.relatedTarget)) return;
        this.scheduleHideTooltip(point);
      });

      const popover = this.getPopover(point);
      if (!popover) return;

      popover.addEventListener('mouseenter', () => this.showTooltip(point));
      popover.addEventListener('mouseleave', () => this.scheduleHideTooltip(point));
      popover.addEventListener('focusin', () => this.showTooltip(point));
      popover.addEventListener('focusout', (event) => {
        if (point.contains(event.relatedTarget)) return;
        this.scheduleHideTooltip(point);
      });
    });

    this.onResize = () => {
      this.updateArrows();
      if (this.activeTooltipPoint) {
        this.positionTooltip(this.activeTooltipPoint);
      }
    };

    this.onDocumentClick = (event) => {
      if (window.innerWidth > 989) return;
      if (
        event.target.closest('.piece-stl__point') ||
        event.target.closest('.piece-stl__product-popover')
      ) {
        return;
      }
      this.clearActiveHotspots();
    };
    document.addEventListener('click', this.onDocumentClick);

    if (this.grid) {
      this.onGridScroll = () => {
        this.updateArrows();
        this.hideAllTooltips();
      };
      this.grid.addEventListener('scroll', this.onGridScroll);
      setTimeout(() => this.updateArrows(), 100);
    }

    window.addEventListener('resize', this.onResize);

    this.onKeydown = this.onKeydown.bind(this);
    document.addEventListener('keydown', this.onKeydown);

    this.preloadDrawerMedia();
  }

  _teardown() {
    this.closeDrawer({ immediate: true });
    this.hideAllTooltips();
    document.removeEventListener('keydown', this.onKeydown);
    document.removeEventListener('click', this.onDocumentClick);
    if (this.grid) {
      this.grid.removeEventListener('scroll', this.onGridScroll);
    }
    window.removeEventListener('resize', this.onResize);
  }

  portalPopovers() {
    this.cards.forEach((card) => {
      const layer =
        card.querySelector('[data-piece-stl-popover-layer]') ||
        this.createPopoverLayer(card);

      card.querySelectorAll('.piece-stl__point').forEach((point) => {
        const popover = point.querySelector('.piece-stl__product-popover');
        if (!popover) return;

        const tile = point.closest('.piece-stl__tile');
        popover.dataset.stlPlacement = tile?.classList.contains('piece-stl__tile--1')
          ? 'hero'
          : 'side';

        if (point.classList.contains('is-sold-out')) {
          popover.classList.add('is-sold-out');
        }

        layer.appendChild(popover);
        this.popoverByPoint.set(point, popover);
        this.pointByPopover.set(popover, point);
      });
    });
  }

  createPopoverLayer(card) {
    const layer = document.createElement('div');
    layer.className = 'piece-stl__popover-layer';
    layer.dataset.pieceStlPopoverLayer = '';
    card.appendChild(layer);
    return layer;
  }

  preloadDrawerMedia() {
    const selectors = [
      '.piece-stl__drawer-lifestyle',
      '.piece-stl__drawer-product-image'
    ].join(',');

    this.querySelectorAll(selectors).forEach((img) => {
      if (!(img instanceof HTMLImageElement) || img.complete) return;

      const loader = new Image();
      const src = img.currentSrc || img.src;
      if (!src) return;

      loader.decoding = 'async';
      loader.src = src;
      if (img.srcset) {
        loader.srcset = img.srcset;
      }
    });
  }

  getPopover(point) {
    return this.popoverByPoint.get(point) || point.querySelector('.piece-stl__product-popover');
  }

  cancelHideTooltip(point) {
    const timer = this.hideTooltipTimers.get(point);
    if (timer) {
      window.clearTimeout(timer);
      this.hideTooltipTimers.delete(point);
    }
  }

  scheduleHideTooltip(point) {
    this.cancelHideTooltip(point);

    if (point.classList.contains('is-active') && window.innerWidth <= 989) {
      return;
    }

    const timer = window.setTimeout(() => {
      this.hideTooltip(point);
      this.hideTooltipTimers.delete(point);
    }, 80);

    this.hideTooltipTimers.set(point, timer);
  }

  showTooltip(point) {
    this.cancelHideTooltip(point);

    if (this.activeTooltipPoint && this.activeTooltipPoint !== point) {
      this.hideTooltip(this.activeTooltipPoint);
    }

    const popover = this.getPopover(point);
    if (!popover) return;

    this.activeTooltipPoint = point;
    popover.classList.add('is-open');
    this.positionTooltip(point);
    requestAnimationFrame(() => {
      if (this.activeTooltipPoint === point) {
        this.positionTooltip(point);
      }
    });
  }

  hideTooltip(point) {
    this.cancelHideTooltip(point);
    const popover = this.getPopover(point);
    popover?.classList.remove('is-open');

    if (this.activeTooltipPoint === point) {
      this.activeTooltipPoint = null;
    }
  }

  hideAllTooltips() {
    this.tooltips.forEach((point) => this.hideTooltip(point));
  }

  positionTooltip(point) {
    const card = point.closest('[data-piece-stl-card]');
    const tile = point.closest('.piece-stl__tile');
    const tooltip = this.getPopover(point);
    if (!card || !tooltip) return;

    const cardRect = card.getBoundingClientRect();
    const pointRect = point.getBoundingClientRect();
    const isHeroTile = tile?.classList.contains('piece-stl__tile--1');
    const gap = 8;
    const edge = 8;

    tooltip.style.left = '0px';
    tooltip.style.top = '0px';
    tooltip.style.transform = 'none';

    const tipRect = tooltip.getBoundingClientRect();
    const tipW = tipRect.width || 170;
    const tipH = tipRect.height || 72;

    const pointCenterX = pointRect.left - cardRect.left + pointRect.width / 2;
    const pointCenterY = pointRect.top - cardRect.top + pointRect.height / 2;
    const pointTop = pointRect.top - cardRect.top;
    const pointBottom = pointRect.bottom - cardRect.top;
    const pointLeft = pointRect.left - cardRect.left;
    const pointRight = pointRect.right - cardRect.left;

    let left;
    let top;

    if (isHeroTile) {
      left = pointCenterX - tipW / 2;
      top = pointBottom + gap;

      if (pointCenterY > cardRect.height / 2 || top + tipH > cardRect.height - edge) {
        top = pointTop - tipH - gap;
      }

      if (left + tipW > cardRect.width - edge) {
        left = pointRight - tipW;
      } else if (left < edge) {
        left = pointLeft;
      }
    } else {
      left = pointLeft - tipW - gap;
      top = pointCenterY - tipH / 2;

      if (left < edge) {
        left = pointRight + gap;
      }
    }

    left = Math.max(edge, Math.min(left, cardRect.width - tipW - edge));
    top = Math.max(edge, Math.min(top, cardRect.height - tipH - edge));

    tooltip.style.left = `${left}px`;
    tooltip.style.top = `${top}px`;
  }

  updateArrows() {
    if (!this.grid || !this.prevButton || !this.nextButton) return;
    const maxScroll = this.grid.scrollWidth - this.grid.clientWidth;
    this.prevButton.disabled = this.grid.scrollLeft <= 4;
    this.nextButton.disabled = this.grid.scrollLeft >= maxScroll - 4;
  }

  scrollTrack(direction) {
    if (!this.grid || this.cards.length === 0) return;

    const gridLeft = this.grid.getBoundingClientRect().left;
    const targets = this.cards.map((card) => card.getBoundingClientRect().left - gridLeft + this.grid.scrollLeft);
    const current = this.grid.scrollLeft;
    let targetIndex = 0;

    for (let i = 0; i < targets.length; i++) {
      if (Math.abs(targets[i] - current) < Math.abs(targets[targetIndex] - current)) {
        targetIndex = i;
      }
    }

    if (direction > 0) {
      if (current > targets[targetIndex] + 8 && targetIndex < targets.length - 1) {
        targetIndex += 1;
      } else {
        targetIndex = Math.min(targetIndex + 1, targets.length - 1);
      }
    } else {
      if (current < targets[targetIndex] - 8 && targetIndex > 0) {
        targetIndex -= 1;
      } else {
        targetIndex = Math.max(targetIndex - 1, 0);
      }
    }

    this.currentIndex = targetIndex;
    this.grid.scrollTo({ left: targets[targetIndex], behavior: 'smooth' });
  }

  openDrawer(index) {
    const drawer = this.drawers.find((d) => Number(d.dataset.lookIndex) === index);
    if (!drawer) return;

    const hasProducts = drawer.querySelector('[data-piece-stl-drawer-item]');
    if (!hasProducts && !this.isDesignMode) return;

    this.closeDrawer({ immediate: true });
    this.clearActiveHotspots();

    if (typeof drawer.showModal === 'function') {
      drawer.showModal();
    }

    this.activeDrawer = drawer;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        drawer.classList.add('is-open');
      });
    });
  }

  closeDrawer({ immediate = false } = {}) {
    this.drawers.forEach((drawer) => {
      window.clearTimeout(drawer._stlCloseTimer);

      if (!drawer.open && !drawer.classList.contains('is-open')) return;

      const finishClose = () => {
        drawer.classList.remove('is-open');
        if (drawer.open && typeof drawer.close === 'function') {
          drawer.close();
        }
        delete drawer._stlCloseTimer;
      };

      drawer.classList.remove('is-open');

      if (immediate || window.innerWidth > 768) {
        finishClose();
        return;
      }

      drawer._stlCloseTimer = window.setTimeout(finishClose, 350);
    });

    this.clearActiveHotspots();
    this.activeDrawer = null;
  }

  clearActiveHotspots(exceptPoint = null) {
    this.tooltips.forEach((point) => {
      if (point !== exceptPoint) {
        point.classList.remove('is-active');
        this.hideTooltip(point);
      }
    });
  }

  handleHotspotTap(event) {
    if (window.innerWidth > 989) return;

    event.preventDefault();
    event.stopPropagation();

    const point = event.currentTarget.closest('.piece-stl__point');
    if (!point) return;

    const isActive = point.classList.contains('is-active');
    this.clearActiveHotspots(point);

    if (isActive) {
      point.classList.remove('is-active');
      this.hideTooltip(point);
      return;
    }

    point.classList.add('is-active');
    this.showTooltip(point);
  }

  onKeydown(event) {
    if (event.key !== 'Escape') return;
    this.clearActiveHotspots();
  }
}

if (!customElements.get('piece-ssp-shop-the-look')) {
  customElements.define('piece-ssp-shop-the-look', PieceSspShopTheLook);
}
